/**
 * Phase 18 — the list ranking as stored and its one ordering rule, shared by
 * the planner and the editor's queue: ranked lists best first, then the
 * unranked ones in the default order they already had (biggest first) — a
 * list with too little traffic is never pushed to the back as "bad".
 * For Media Devoted | Created By Ciro Autiero
 */
import { defaultHourFormula } from "../hour-formula";
import { notEnoughDataRule, rulesWithLegacyFloor } from "../rules/default-rules";
import { parseRules } from "../rules/parse-rules";
import { DROP_GEN_LIST_RULE_ACTIONS, DROP_GEN_TAGS_CHECK } from "../rules/rules.types";
import type { DropGenListRanking, DropGenRankedList } from "./list-ranking.types";

export const DROP_GEN_LIST_RANKING_LIMITS = { listsMax: 2000 } as const;

/** The old "Min volume / list" default — the default exclude rule's number. */
export const DEFAULT_LIST_FLOOR = 100;

/** Ranked on CR over the last 30 days; "Not enough data" under 100 unique visits. */
export function defaultListRanking(): DropGenListRanking {
  const formula = defaultHourFormula("last-30-days", "cr");
  const id = formula.groups[0].id;
  formula.groups[0] = { ...formula.groups[0], id: "lists-1", name: "Lists" };
  formula.groupWeights = { "lists-1": formula.groupWeights[id] ?? 100 };
  return { formula, rules: [notEnoughDataRule(DEFAULT_LIST_FLOOR)] };
}

/** Stored JSON → a usable config; anything malformed falls back piece by piece. */
export function parseListRanking(json: unknown): DropGenListRanking {
  const base = defaultListRanking();
  if (typeof json !== "object" || json === null) return base;
  const r = json as Record<string, unknown>;
  const f = r.formula as DropGenListRanking["formula"] | undefined;
  const formula = f && Array.isArray(f.groups) && f.groups.length > 0 ? { groups: f.groups, groupWeights: f.groupWeights ?? {} } : base.formula;
  return { formula, rules: parseRules(rulesWithLegacyFloor(r, "minVolume", DEFAULT_LIST_FLOOR), DROP_GEN_LIST_RULE_ACTIONS, DROP_GEN_TAGS_CHECK) };
}

type Ranked = Pick<DropGenRankedList, "wiserId" | "score" | "unranked">;

/** The lists in formula order: ranked best first, the unranked as `defaultOrdered` had them. */
export function listFormulaOrder(defaultOrdered: number[], ranked: Ranked[]): number[] {
  const score = new Map(ranked.filter((r) => !r.unranked && r.score !== null).map((r) => [r.wiserId, r.score!]));
  const scored = defaultOrdered.filter((id) => score.has(id));
  scored.sort((a, b) => score.get(b)! - score.get(a)!);
  return [...scored, ...defaultOrdered.filter((id) => !score.has(id))];
}
